import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Box, Collapse, Typography } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { useLang } from '../../context/LanguageContext';
import { useTranslation } from '../../content/useTranslation';
import { scrollToSectionById, setPendingHomeScroll } from '../../utils/smoothScroll';
import { openCookieSettings } from '../../utils/cookieConsent';
import { useMenu } from '../../context/MenuContext';
import { LEGAL_NAV, MAIN_NAV } from '../../navigation/siteNavigation';
import { isHomePath, PATHS } from '../../navigation/paths';
import { TopBarTheme } from '../../utils/topBarSectionColor';
import { COLORS, FONTS, SPACING } from '../../theme/designTokens';
import { MOTION_EASE } from '../motion/easing';
import BurgerButton from './BurgerButton';
import SiteLogo from './SiteLogo';
import { TOPBAR_HEIGHT } from './SiteTopBar';

const MotionPanel = motion.create(Box);

function resolveMenuBg(bg: string, theme: TopBarTheme): string {
  if (bg && bg !== 'transparent') return bg;
  return theme === 'dark' ? COLORS.bgDark : COLORS.bg;
}

const SiteMenu: React.FC = () => {
  const { menuOpen, closeMenu, toggleMenu, appearance } = useMenu();
  const { navLabel, legalLabel, lang } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = isHomePath(location.pathname);
  const homePath = lang === 'en' ? PATHS.en : PATHS.home;
  const isDark = appearance.theme === 'dark';
  const menuBg = resolveMenuBg(appearance.bg, appearance.theme);

  const textPrimary = isDark ? COLORS.textOnDark : '#393939';
  const textSecondary = isDark ? 'rgba(255,255,255,0.88)' : '#3a3a3a';
  const borderColor = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(93,93,93,0.12)';

  const [openSection, setOpenSection] = useState<string | null>(null);

  useEffect(() => {
    closeMenu();
    setOpenSection(null);
  }, [location.pathname, closeMenu]);

  const scrollToSection = useCallback(
    (path: string) => {
      const hash = path.startsWith('/#') ? path.slice(2) : null;
      closeMenu();

      if (!hash) {
        navigate(path);
        return;
      }

      const isLeistung = hash.startsWith('leistung-');
      const slug = isLeistung ? hash.replace('leistung-', '') : null;

      const doScroll = () => {
        if (isLeistung && slug) {
          window.dispatchEvent(new CustomEvent('leistung-open', { detail: slug }));
        } else {
          scrollToSectionById(hash, { initialDelay: 0 });
        }
      };

      if (isHome) {
        doScroll();
      } else {
        setPendingHomeScroll(hash);
        navigate(homePath);
      }
    },
    [closeMenu, homePath, isHome, navigate],
  );

  if (typeof document === 'undefined') return null;

  const menuItems = MAIN_NAV.filter((item) => item.path !== PATHS.home);
  const isAnchor = (path: string) => path.startsWith('/#');

  return createPortal(
    <AnimatePresence initial={false}>
      {menuOpen && (
        <MotionPanel
          key="site-menu-panel"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: MOTION_EASE }}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 10100,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            backgroundColor: menuBg,
            px: SPACING.sectionPaddingX,
          }}
        >
          <Box
            sx={{
              minHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr',
                alignItems: 'center',
                height: `${TOPBAR_HEIGHT}px`,
                pt: '12px',
                flexShrink: 0,
              }}
            >
              <Box sx={{ justifySelf: 'start' }}>
                <BurgerButton open onClick={toggleMenu} color={textPrimary} />
              </Box>

              <Box
                component="button"
                type="button"
                onClick={() => {
                  closeMenu();
                  if (isHome) {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  } else {
                    navigate(homePath);
                  }
                }}
                sx={{
                  justifySelf: 'center',
                  display: 'flex',
                  textDecoration: 'none',
                  background: 'none',
                  border: 'none',
                  p: 0,
                  cursor: 'pointer',
                }}
              >
                <SiteLogo onDark={isDark} />
              </Box>

              <Box sx={{ justifySelf: 'end' }} />
            </Box>

            <Box
              component="nav"
              sx={{
                mt: { xs: '40px', md: '56px' },
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
              }}
            >
              {menuItems.map((section) => {
                const hasChildren = section.children && section.children.length > 0;
                const sectionIsOpen = openSection === section.path;
                const anchor = isAnchor(section.path);

                return (
                  <Box key={section.path}>
                    <Typography
                      component="button"
                      type="button"
                      onClick={() => {
                        if (hasChildren) {
                          setOpenSection(sectionIsOpen ? null : section.path);
                        } else if (anchor) {
                          scrollToSection(section.path);
                        } else {
                          closeMenu();
                          navigate(section.path);
                        }
                      }}
                      sx={{
                        fontFamily: FONTS.serif,
                        fontWeight: 300,
                        fontSize: { xs: '28px', md: '36px' },
                        lineHeight: 1.2,
                        letterSpacing: '-0.3px',
                        color: textPrimary,
                        textDecoration: 'none',
                        display: 'block',
                        py: { xs: '10px', md: '12px' },
                        px: 0,
                        background: 'none',
                        border: 'none',
                        textAlign: 'left',
                        width: '100%',
                        cursor: 'pointer',
                        transition: 'opacity 0.2s ease',
                        '&:hover': { opacity: 0.55 },
                      }}
                    >
                      {navLabel(section.label)}
                    </Typography>

                    {hasChildren && (
                      <Collapse in={sectionIsOpen} timeout={300}>
                        <Box
                          component="ul"
                          sx={{
                            m: 0,
                            p: 0,
                            pb: '8px',
                            listStyle: 'none',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                            pl: '2px',
                          }}
                        >
                          <Box component="li">
                            <Typography
                              component="button"
                              type="button"
                              onClick={() => scrollToSection(section.path)}
                              sx={{
                                fontFamily: FONTS.sans,
                                fontWeight: 400,
                                fontSize: { xs: '14px', md: '15px' },
                                lineHeight: 1.5,
                                color: textPrimary,
                                textDecoration: 'none',
                                display: 'block',
                                background: 'none',
                                border: 'none',
                                p: 0,
                                cursor: 'pointer',
                                textAlign: 'left',
                                transition: 'opacity 0.2s ease',
                                '&:hover': { opacity: 0.55 },
                              }}
                            >
                              Übersicht
                            </Typography>
                          </Box>
                          {section.children!.map((child) => {
                            const childIsAnchor = isAnchor(child.path);
                            return (
                              <Box component="li" key={child.path}>
                                <Typography
                                  component={childIsAnchor ? 'button' : Link}
                                  {...(childIsAnchor
                                    ? {
                                        type: 'button' as const,
                                        onClick: () => scrollToSection(child.path),
                                      }
                                    : {
                                        to: child.path,
                                        onClick: closeMenu,
                                      })}
                                  sx={{
                                    fontFamily: FONTS.sans,
                                    fontWeight: 300,
                                    fontSize: { xs: '14px', md: '15px' },
                                    lineHeight: 1.5,
                                    color: textSecondary,
                                    textDecoration: 'none',
                                    display: 'block',
                                    background: 'none',
                                    border: 'none',
                                    p: 0,
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    transition: 'color 0.2s ease',
                                    '&:hover': { color: textPrimary },
                                  }}
                                >
                                  {child.label}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Collapse>
                    )}
                  </Box>
                );
              })}
            </Box>

            <LangSwitch textPrimary={textPrimary} onSwitch={closeMenu} />

            <Box
              sx={{
                pt: '20px',
                pb: { xs: '32px', md: '40px' },
                borderTop: `1px solid ${borderColor}`,
                display: 'flex',
                flexWrap: 'wrap',
                gap: { xs: '20px', md: '36px' },
              }}
            >
              {LEGAL_NAV.map((link) => (
                <Typography
                  key={link.path}
                  component={Link}
                  to={link.path}
                  onClick={closeMenu}
                  sx={{
                    fontFamily: FONTS.sans,
                    fontWeight: 300,
                    fontSize: '13px',
                    lineHeight: 1.4,
                    letterSpacing: '1.6px',
                    textTransform: 'uppercase',
                    color: textSecondary,
                    textDecoration: 'none',
                    transition: 'color 0.2s ease',
                    '&:hover': { color: textPrimary },
                  }}
                >
                  {legalLabel(link.label)}
                </Typography>
              ))}
              <Typography
                component="button"
                type="button"
                onClick={() => {
                  closeMenu();
                  openCookieSettings();
                }}
                sx={{
                  fontFamily: FONTS.sans,
                  fontWeight: 300,
                  fontSize: '13px',
                  lineHeight: 1.4,
                  letterSpacing: '1.6px',
                  textTransform: 'uppercase',
                  color: textSecondary,
                  textDecoration: 'none',
                  background: 'none',
                  border: 0,
                  p: 0,
                  cursor: 'pointer',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: textPrimary },
                }}
              >
                {legalLabel('Cookie-Einstellungen')}
              </Typography>
            </Box>
          </Box>
        </MotionPanel>
      )}
    </AnimatePresence>,
    document.body,
  );
};

const LANG_OPTIONS: { code: 'de' | 'en'; label: string }[] = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
];

const LangSwitch: React.FC<{ textPrimary: string; onSwitch: () => void }> = ({
  textPrimary,
  onSwitch,
}) => {
  const { lang, setLang } = useLang();
  const location = useLocation();
  const navigate = useNavigate();

  const handleSelect = (code: 'de' | 'en') => {
    if (lang === code) return;
    setLang(code);
    const path = location.pathname.replace(/\/+$/, '') || '/';
    if (code === 'en' && path === '/') navigate(PATHS.en);
    if (code === 'de' && path === PATHS.en) navigate(PATHS.home);
    onSwitch();
  };

  return (
    <Box
      sx={{
        mt: 'auto',
        pb: { xs: '20px', md: '24px' },
        display: 'flex',
        alignItems: 'baseline',
        gap: { xs: '16px', md: '20px' },
      }}
    >
      {LANG_OPTIONS.map(({ code, label }) => {
        const isActive = lang === code;
        return (
          <Typography
            key={code}
            component="button"
            type="button"
            aria-pressed={isActive}
            onClick={() => handleSelect(code)}
            sx={{
              fontFamily: FONTS.serif,
              fontWeight: 300,
              fontSize: { xs: '18px', md: '22px' },
              lineHeight: 1.2,
              letterSpacing: '-0.2px',
              color: textPrimary,
              opacity: isActive ? 1 : 0.35,
              background: 'none',
              border: 'none',
              cursor: isActive ? 'default' : 'pointer',
              p: 0,
              transition: 'opacity 0.2s ease',
              '&:hover': {
                opacity: isActive ? 1 : 0.55,
              },
            }}
          >
            {label}
          </Typography>
        );
      })}
    </Box>
  );
};

export default SiteMenu;
